import type { Website } from "@/constants/features";
import * as api from "../client";
import type {
  Article,
  ArticleType,
  ContentStatus,
  ListResponse,
  RegionAssignmentInput,
  StatusUpdatePayload,
} from "./types";

const BASE = "/v1/profiles/articles";

/**
 * Widened article-type union owned by this service.
 *
 * The shared `ArticleType` in `api/profiles/types.ts` only carries three values
 * (`focus_on | doing_business_in | interview`) and that file is owned by a
 * different phase, so it can't be edited here. profiles-api already accepts the
 * two additional types, so the staff Article form needs the full five-value
 * union. We define it locally and widen the create/update payload `articleType`
 * to it; existing service signatures that still type `articleType` as the
 * 3-value `ArticleType` are narrowed/cast at the call site where needed.
 */
export type StaffArticleType =
  | "doing_business_in"
  | "focus_on"
  | "interview"
  | "press_release"
  | "legal_development";

/**
 * Local read-side augmentation of the shared `Article`. Retained for
 * back-compat: the shared `Article` type now carries the inline
 * `featuredImageHash`/`featuredImageFilename`, `languageCode`, `website`,
 * `editorialCountryCode`, and `editorialCategories` directly, so this alias is
 * largely redundant, but existing call sites still reference it.
 */
export type StaffArticleHydration = Article & {
  languageCode?: string | null;
  website?: Website | null;
  editorialCountryCode?: string | null;
};

export type ArticleFilters = {
  companyId?: string;
  articleType?: ArticleType;
  status?: string;
  /** Free-text search (passed as `q`). API support may be partial. */
  q?: string;
  /**
   * Region filter (§3.4): `regionKind` + `regionId` (integer) together or
   * neither. `regionId` is the integer `api_region_country_v2` id.
   */
  regionKind?: "region" | "country";
  regionId?: string;
  limit?: number;
  offset?: number;
};

export type ArticleCreatePayload = {
  companyId: string;
  title: string;
  // Widened to the staff 5-value union so press_release / legal_development can
  // be sent. profiles-api accepts all five.
  articleType: StaffArticleType;
  languageCode?: string;
  /** Publishing target / edition (Issue 3). Defaults server-side to `com_en`. */
  website?: Website;
  content?: string;
  status?: ContentStatus;
  featuredImageHash?: string | null;
  featuredImageFilename?: string | null;
  editorialCountryCode?: string;
  contactId?: string;
  /** Editorial-category joins (DBI / focus_on only). */
  editorialCategories?: Array<{ editorialCategoryId: number }>;
  /** At least one region required server-side. */
  regions?: RegionAssignmentInput[];
};

export type ArticleUpdatePayload = Partial<Omit<ArticleCreatePayload, "companyId">>;

function buildQuery(filters: ArticleFilters): string {
  const params = new URLSearchParams();
  if (filters.companyId) params.append("companyId", filters.companyId);
  if (filters.articleType) params.append("articleType", filters.articleType);
  if (filters.status) params.append("status", filters.status);
  if (filters.q) params.append("search", filters.q);
  if (filters.regionKind && filters.regionId) {
    params.append("regionKind", filters.regionKind);
    params.append("regionId", filters.regionId);
  }
  if (filters.limit !== undefined) params.append("limit", String(filters.limit));
  if (filters.offset !== undefined) params.append("offset", String(filters.offset));
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const articlesService = {
  async list(filters: ArticleFilters = {}): Promise<ListResponse<Article>> {
    const raw = await api.get<{
      firmArticles: Article[];
      pagination: { total: number; limit: number; offset: number };
    }>(`${BASE}${buildQuery(filters)}`);
    return {
      data: raw.firmArticles ?? [],
      total: raw.pagination?.total ?? 0,
      limit: raw.pagination?.limit ?? 20,
      offset: raw.pagination?.offset ?? 0,
    };
  },

  async get(id: string): Promise<Article> {
    const raw = await api.get<{ firmArticle: Article } | Article>(`${BASE}/${id}`);
    return (raw as { firmArticle?: Article }).firmArticle ?? (raw as Article);
  },

  create(payload: ArticleCreatePayload): Promise<Article> {
    return api.post<Article>(BASE, payload);
  },

  update(id: string, payload: ArticleUpdatePayload): Promise<Article> {
    return api.put<Article>(`${BASE}/${id}`, payload);
  },

  remove(id: string): Promise<void> {
    return api.del<void>(`${BASE}/${id}`);
  },

  async updateStatus(id: string, payload: StatusUpdatePayload): Promise<Article> {
    const raw = await api.patch<{ firmArticle: Article } | Article>(
      `${BASE}/${id}/status`,
      payload,
    );
    return (raw as { firmArticle?: Article }).firmArticle ?? (raw as Article);
  },
};
